import React, { useMemo } from 'react';
import { Control, useFieldArray, useWatch } from 'react-hook-form';
import { UserInviteTeamMemberForm } from '../InviteTeamMemberModal';
import styles from './InviteTeamMemberForm.module.scss';
import classnames from 'classnames';
import { Button } from 'react-bootstrap';
import { Organization } from 'v2/models';
import InviteTeamMemberFormFields from './InviteTeamMemberFormFields/InviteTeamMemberFormFields';
import {
  MAX_EMAIL_INVITATIONS_PER_ORGANIZATION,
  MAX_EMAIL_INVITATIONS_PER_SUBMIT,
} from 'utils/constants';
import { FEATURE_FLAGS } from 'utils/constants';
import { useFeatureFlags } from 'v2/hooks/useFeatureFlags';
import InviteTeamMemberFormFieldsLicenses from './InviteTeamMemberFormFields/InviteTeamMemberFormFieldsLicenses';

type InviteTeamMemberFormProps = {
  control: Control<UserInviteTeamMemberForm>;
  organizations: Organization[];
};

const InviteTeamMemberForm = ({
  organizations,
  control,
}: InviteTeamMemberFormProps) => {
  const bulkInvitations = useWatch({ control, name: 'bulkInvitations' });
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'bulkInvitations',
    rules: {
      validate: (bulkInvitations) =>
        bulkInvitations.some(
          (bulkInvitation) => bulkInvitation.inputEmails.emails.length > 0
        ),
    },
  });
  const options = useMemo(() => {
    return organizations.map(({ id, name }) => ({
      label: name,
      value: id,
    }));
  }, [organizations]);

  const controlledFields = fields.map((field, index) => {
    return {
      ...field,
      ...bulkInvitations[index],
    };
  });

  const [isInviteColleagueEnabled] = useFeatureFlags([
    FEATURE_FLAGS.INVITE_COLLEAGUE_WITH_LICENSE,
  ]);

  const selectedOrganizationsSet = new Set(
    bulkInvitations.map((bulkInvitation) => bulkInvitation.organization)
  );
  const noSelectedOrganizations = organizations.filter(
    (organization) => !selectedOrganizationsSet.has(organization.id)
  );
  const hasMultipleOrganizations = organizations.length > 1;

  const canAddOrganization =
    hasMultipleOrganizations && noSelectedOrganizations.length > 0;

  const showRemoveButton =
    isInviteColleagueEnabled && bulkInvitations.length > 1;
  return (
    <div className={classnames(styles.form)}>
      <p className={classnames('text-light4', styles.intro)}>
        Please provide email addresses of the team members you would like to
        invite below. You are allowed to invite up to{' '}
        {MAX_EMAIL_INVITATIONS_PER_ORGANIZATION} team members at a time
      </p>
      <div className={styles.inputsContainer}>
        {controlledFields.map((field, index) => (
          <div key={field.id}>
            {isInviteColleagueEnabled ? (
              <InviteTeamMemberFormFieldsLicenses
                showOrganizationDropdown={hasMultipleOrganizations}
                selectedOption={options.find(
                  (option) => option.value === field.organization
                )}
                organizations={organizations}
                index={index}
                control={control}
              />
            ) : (
              <InviteTeamMemberFormFields
                showOrganizationDropdown={hasMultipleOrganizations}
                selectedOption={options.find(
                  (option) => option.value === field.organization
                )}
                organizations={noSelectedOrganizations}
                index={index}
                control={control}
              />
            )}
            {showRemoveButton && (
              <>
                <Button
                  variant="link"
                  onClick={() => remove(index)}
                  className={classnames('p-0 mr-auto', styles.removeButton)}
                >
                  Remove
                </Button>
                <div className={styles.line}></div>
              </>
            )}
          </div>
        ))}
      </div>
      {isInviteColleagueEnabled ? (
        <>
          {controlledFields.length < MAX_EMAIL_INVITATIONS_PER_SUBMIT && (
            <Button
              className="p-0 mr-auto"
              variant="link"
              onClick={() =>
                append({
                  organization: organizations[0].id,
                  inputEmails: {
                    emails: [''],
                    inputValue: '',
                  },
                  licenses: [],
                })
              }
            >
              + Add another Team Member
            </Button>
          )}
        </>
      ) : (
        <>
          {canAddOrganization && (
            <Button
              className="p-0 mr-auto"
              variant="link"
              onClick={() =>
                append({
                  organization: noSelectedOrganizations[0].id,
                  inputEmails: {
                    emails: [],
                    inputValue: '',
                  },
                })
              }
            >
              + Add Organization
            </Button>
          )}
        </>
      )}
    </div>
  );
};

export default InviteTeamMemberForm;
